'use client';

import React, { useId, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { MoreControls } from './more-controls';
import { Sheet } from './sheet';
import { useCompositionDensity } from './composition-density';
import type { ControlSize } from './control-styles';

export interface RegisterFilter {
  id: string;
  label: string;
  control: React.ReactNode;
  active?: boolean;
}
export interface RegisterExportAction {
  id: string;
  label: string;
  onSelect: () => void | Promise<void>;
  disabledReason?: string;
}
export interface RegisterToolbarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchLabel?: string;
  ariaLabel?: string;
  filters?: readonly RegisterFilter[];
  /** Controlled filters displayed in the toolbar row before the filter sheet. */
  inlineFilters?: React.ReactNode;
  /** Result count, owned by the caller. For server paging pass "N on this page". */
  count?: React.ReactNode;
  /** Backwards-compatible result-count slot. */
  scope?: React.ReactNode;
  size?: ControlSize;
  columns?: React.ReactNode;
  tools?: React.ReactNode;
  exports?: readonly RegisterExportAction[];
  onClear?: () => void;
  /** Use a native GET form for URL-parameter filters. */
  form?: {
    action?: string;
    method?: 'get';
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
  };
  submitLabel?: string;
  className?: string;
}

/** Filtering and result counts remain owned by the caller. */
export function RegisterToolbar({
  search,
  onSearchChange,
  searchLabel = 'Search records',
  ariaLabel = 'Register search and filters',
  filters = [],
  inlineFilters,
  scope,
  count: resultCount,
  size = 'toolbar',
  columns,
  tools,
  exports = [],
  onClear,
  form,
  submitLabel = 'Apply',
  className,
}: RegisterToolbarProps) {
  const [drawer, setDrawer] = useState(false);
  const [pendingExport, setPendingExport] = useState<string | null>(null);
  const pendingExportRef = useRef<string | null>(null);
  const searchId = useId();
  const density = useCompositionDensity();
  const count = filters.filter((filter) => filter.active).length;
  const hasSheet = !inlineFilters || filters.length > 0;
  const hasView = !inlineFilters || Boolean(columns || tools);
  const hasClear = Boolean(onClear && (count > 0 || (search?.length ?? 0) > 0));
  const selectExport = (action: RegisterExportAction) => {
    if (pendingExportRef.current || action.disabledReason !== undefined) return;
    const result = action.onSelect();
    if (!result) return;
    pendingExportRef.current = action.id;
    setPendingExport(action.id);
    void result.finally(() => {
      pendingExportRef.current = null;
      setPendingExport(null);
    });
  };
  const content = (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {search !== undefined && onSearchChange && (
          <div className="min-w-0 basis-64 grow">
            <label htmlFor={searchId} className="sr-only">
              {searchLabel}
            </label>
            <Input
              id={searchId}
              type="search"
              size={size}
              density={density}
              value={search}
              placeholder={searchLabel}
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </div>
        )}
        {inlineFilters && (
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
            {inlineFilters}
          </div>
        )}
        {(hasSheet || hasView || hasClear || form || exports.length > 0) && (
          <div
            aria-label="Register controls"
            className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-2"
          >
            {hasSheet && (
              <Button
                type="button"
                variant="outline"
                size={size}
                density={density}
                onClick={() => setDrawer(true)}
              >
                Filters{count > 0 ? ` (${count})` : ''}
              </Button>
            )}
            {exports.length > 0 && (
              <MoreControls label="Export" size={size} density={density}>
                {exports.map((action) => (
                  <Button
                    key={action.id}
                    type="button"
                    variant="ghost"
                    size={size}
                    density={density}
                    isLoading={pendingExport === action.id}
                    {...(action.disabledReason !== undefined
                      ? { disabledReason: action.disabledReason || 'Export unavailable' }
                      : {})}
                    onClick={() => selectExport(action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </MoreControls>
            )}
            {hasView && (
              <div
                aria-label="Columns and tools"
                className="flex min-w-0 max-w-full flex-wrap items-center gap-2"
              >
                <MoreControls label="View" size={size} density={density}>
                  {columns || (
                    <p className="p-2 text-body text-text-muted">No additional view options.</p>
                  )}
                  {tools}
                </MoreControls>
              </div>
            )}
            {hasClear && (
              <Button
                type="button"
                variant="tertiary"
                size={size}
                density={density}
                onClick={onClear}
              >
                Clear
              </Button>
            )}
            {form && (
              <Button type="submit" size={size} density={density}>
                {submitLabel}
              </Button>
            )}
          </div>
        )}
        {(!inlineFilters || resultCount != null || scope != null || exports.length > 0) && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="shrink-0 text-caption text-text-muted"
          >
            {pendingExport ? 'Preparing export' : (resultCount ?? scope)}
          </div>
        )}
      </div>
      {hasSheet && (
        <Sheet
          open={drawer}
          onClose={() => setDrawer(false)}
          title="Filters"
          description={`${count} active ${count === 1 ? 'filter' : 'filters'}`}
          footer={
            onClear ? (
              <Button type="button" variant="secondary" density={density} onClick={onClear}>
                Clear filters
              </Button>
            ) : undefined
          }
        >
          <div className="min-w-0 space-y-4">
            {filters.map((filter) => (
              <div
                key={filter.id}
                role="group"
                aria-label={filter.label}
                className="min-w-0 space-y-2"
              >
                <p className="min-w-0 break-words font-body text-body text-text-heading">
                  {filter.label}
                </p>
                {filter.control}
              </div>
            ))}
          </div>
        </Sheet>
      )}
    </>
  );

  return form ? (
    <form
      role="search"
      aria-label={ariaLabel}
      className={cn('min-w-0 space-y-2', className)}
      action={form.action}
      method={form.method ?? 'get'}
      onSubmit={form.onSubmit}
    >
      {content}
    </form>
  ) : (
    <div role="search" aria-label={ariaLabel} className={cn('min-w-0 space-y-2', className)}>
      {content}
    </div>
  );
}
